/**
 * Tiny wrapper around @capacitor/haptics. All calls are fire-and-forget and
 * no-ops on web — never await, never gate UI on a haptic completing. The
 * plugin is dynamically imported so the web bundle pays nothing.
 *
 * Use sparingly. Haptics that fire on every render or every focus change get
 * annoying fast. Good fits:
 *  - tab bar selection
 *  - destructive action confirmation
 *  - error toast / form validation failure
 *  - completion of a multi-step flow (sign in, post created)
 */

import { isNativePlatform } from '@/lib/platform';

type ImpactStyleName = 'Light' | 'Medium' | 'Heavy';
type NotificationTypeName = 'Success' | 'Warning' | 'Error';

async function impact(style: ImpactStyleName): Promise<void> {
    if (!isNativePlatform()) return;
    try {
        const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
        await Haptics.impact({ style: ImpactStyle[style] });
    } catch {
        /* haptics aren't critical; silently swallow */
    }
}

async function notification(type: NotificationTypeName): Promise<void> {
    if (!isNativePlatform()) return;
    try {
        const { Haptics, NotificationType } = await import('@capacitor/haptics');
        await Haptics.notification({ type: NotificationType[type] });
    } catch {
        /* ignore */
    }
}

/** Light tap — for buttons, tab selection, toggles. */
export function hapticLight(): void {
    void impact('Light');
}

/** Medium tap — for confirmation, longer presses. */
export function hapticMedium(): void {
    void impact('Medium');
}

/** Heavy tap — reserve for irreversible actions. */
export function hapticHeavy(): void {
    void impact('Heavy');
}

/** Success notification — completion of a flow. */
export function hapticSuccess(): void {
    void notification('Success');
}

/** Warning notification — validation failure, recoverable error. */
export function hapticWarning(): void {
    void notification('Warning');
}

/** Error notification — failed operation, destructive interrupt. */
export function hapticError(): void {
    void notification('Error');
}

/** Selection change — light tick for picker/scroll selection. */
export async function hapticSelection(): Promise<void> {
    if (!isNativePlatform()) return;
    try {
        const { Haptics } = await import('@capacitor/haptics');
        await Haptics.selectionStart();
        await Haptics.selectionChanged();
        await Haptics.selectionEnd();
    } catch {
        /* ignore */
    }
}
